import React, { useState, useEffect } from 'react';
import { policiesService, Policy } from '../services/policies.service';
import PolicyDocumentEmbed from './Layout/PolicyDocumentEmbed';
import './PolicyAcceptanceModal.css';

interface PolicyAcceptanceModalProps {
  onAccept: () => void;
}

const PolicyAcceptanceModal: React.FC<PolicyAcceptanceModalProps> = ({ onAccept }) => {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadPolicies = async () => {
      try {
        const status = await policiesService.getUserStatus();
        setPolicies(status.unacceptedPolicies);
      } catch (error) {
        console.error('Failed to load policies:', error);
      }
    };
    loadPolicies();
  }, []);

  const handleCheckboxChange = async (policyId: string, checked: boolean) => {
    if (checked) {
      setAccepted(new Set(accepted).add(policyId));
      // Accept policy immediately when checked
      try {
        await policiesService.acceptPolicy(policyId);
      } catch (error) {
        console.error('Failed to accept policy:', error);
        // Remove from accepted set if failed
        const newAccepted = new Set(accepted);
        newAccepted.delete(policyId);
        setAccepted(newAccepted);
        alert('Failed to accept policy. Please try again.');
        return;
      }
    } else {
      const newAccepted = new Set(accepted);
      newAccepted.delete(policyId);
      setAccepted(newAccepted);
    }
  };

  const handleNext = () => {
    if (currentIndex < policies.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleFinish = async () => {
    setIsSubmitting(true);
    try {
      // Verify all policies are accepted
      const allAccepted = policies.every(p => accepted.has(p.id));
      if (!allAccepted) {
        alert('Please accept all policies to continue.');
        setIsSubmitting(false);
        return;
      }
      onAccept();
    } catch (error) {
      console.error('Failed to complete acceptance:', error);
      alert('Failed to complete. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (policies.length === 0) {
    return null;
  }

  const currentPolicy = policies[currentIndex];
  const isLastPolicy = currentIndex === policies.length - 1;
  const isAccepted = accepted.has(currentPolicy.id);

  return (
    <div className="policy-modal-overlay">
      <div className="policy-modal-content">
        <div className="policy-modal-header">
          <h2>{currentPolicy.title}</h2>
          <div className="policy-counter">
            Policy {currentIndex + 1} of {policies.length}
          </div>
        </div>
        
        <div className="policy-modal-body">
          {currentPolicy.documentUrl && (
            <div className="policy-document-wrap">
              <PolicyDocumentEmbed url={currentPolicy.documentUrl} variant="acceptance" />
            </div>
          )}
          {currentPolicy.content?.trim() ? (
            <div className="policy-content">
              <div
                dangerouslySetInnerHTML={{
                  __html: currentPolicy.content.replace(/\n/g, '<br />'),
                }}
              />
            </div>
          ) : null}
          {!currentPolicy.documentUrl && !currentPolicy.content?.trim() ? (
            <div className="policy-content policy-content--empty">
              <p>No policy text or document attached.</p>
            </div>
          ) : null}

          <div className="policy-acceptance">
            <label className="policy-checkbox-label">
              <input
                type="checkbox"
                checked={isAccepted}
                onChange={(e) => handleCheckboxChange(currentPolicy.id, e.target.checked)}
                disabled={isSubmitting}
              />
              <span>I Agree to {currentPolicy.title}</span>
            </label>
          </div>
        </div>

        <div className="policy-modal-footer">
          {!isLastPolicy && (
            <button
              className="btn-next"
              onClick={handleNext}
              disabled={!isAccepted || isSubmitting}
            >
              Next Policy
            </button>
          )}
          {isLastPolicy && (
            <button
              className="btn-accept"
              onClick={handleFinish}
              disabled={!isAccepted || isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Continue'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PolicyAcceptanceModal;

